/**
 * Format a raw token amount string to a human-readable number.
 * Shifts the decimal point left by `decimals` places and caps display
 * precision based on the value magnitude:
 *   >= 10000 → max 2 decimal places
 *   >= 1     → max 4 decimal places
 *   < 1      → leading zeros + 5 significant digits
 */
export function formatAmount(amount: string, decimals: number): string {
  if (!amount || amount === "0") return "0";

  const neg = amount.startsWith("-");
  let raw = neg ? amount.slice(1) : amount;
  raw = raw.padStart(decimals + 1, "0");

  const intPart = raw.slice(0, raw.length - decimals) || "0";
  const fracFull = raw.slice(raw.length - decimals).replace(/0+$/, "");

  if (!fracFull) return neg ? `-${intPart}` : intPart;

  const intVal = Number(intPart);
  let maxFrac: number;
  if (intVal >= 1) {
    maxFrac = 4;
  } else {
    const leadingZeros = fracFull.match(/^0*/)?.[0].length ?? 0;
    maxFrac = leadingZeros + 5;
  }

  const fracTrimmed = fracFull.slice(0, maxFrac).replace(/0+$/, "");
  const formatted = fracTrimmed ? `${intPart}.${fracTrimmed}` : intPart;
  return neg ? `-${formatted}` : formatted;
}

export const formatTokenAmount = formatAmount;

/** Truncate an address to "0x1234...abcd" (EVM) or "Abc1...xyz9" (base58) */
export function truncateAddress(addr: string): string {
  if (addr.length <= 10) return addr;
  // EVM addresses start with 0x - show 6+4
  if (addr.startsWith("0x") || addr.startsWith("0X")) {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }
  // Base58 (Solana/Tron) - show 4+4
  return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
}

/** Format a numeric string as USD: "$1,234.56" */
export function formatUsd(value: string): string {
  const num = Number(value);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format USD value in compact form: $1.2K, $3.4M */
export function formatCompactUsd(value: number): string {
  if (value < 0.01) return "$0";
  if (value < 1_000) return `$${value.toFixed(2)}`;
  if (value < 1_000_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

/** Format seconds to a human-readable duration: "~6s" or "~2min" */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `~${Math.round(seconds)}s`;
  return `~${Math.round(seconds / 60)}min`;
}
